/* eslint-disable react/prop-types */
import { Card, Nav, Tab } from "react-bootstrap";
import { getMenuLanguages } from "../../../constants/menuLanguages";

import PageTitle from "../../layouts/PageTitle";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import swal from "sweetalert";
import config from "../../../../config.json";
import "../../../i18n";
import { useTranslation } from "react-i18next";
import {
  IMAGE_PICKER_ACCEPT,
  validateImageFile,
} from "../../../utils/imageValidation";
import MenuCustomerDisplaySwitches from "./MenuCustomerDisplaySwitches";
import ProductTitleIconStylePicker from "./ProductTitleIconStylePicker";
import ProductCardSizePicker from "./ProductCardSizePicker";
import MenuTextFontPicker from "./MenuTextFontPicker";
import MenuTitlesAlignPickers from "./MenuTitlesAlignPickers";
import MenuSummaryAlignPicker from "./MenuSummaryAlignPicker";
import MenuSummaryBulletPicker from "./MenuSummaryBulletPicker";
import MenuSummaryPlusPicker from "./MenuSummaryPlusPicker";
import MenuFormDisplaySection from "./MenuFormDisplaySection";
import MenuContentTypeSection from "./MenuContentTypeSection";
import { MENU_CONTENT_TYPES } from "../../../constants/menuContentType";
import MenuTree from "./MenuTree/MenuTree";
import "./MenuFormSidebar.css";
import "./MenuFormShell.css";

function extractCreatedMenuId(payload) {
  if (!payload || typeof payload !== "object") return null;
  const raw =
    payload._id ||
    payload.id ||
    payload.menu?._id ||
    payload.menu?.id ||
    payload.data?._id ||
    payload.data?.id;
  if (!raw) return null;
  const id = typeof raw === "object" && typeof raw.toString === "function" ? raw.toString() : String(raw);
  return id && id !== "[object Object]" ? id : null;
}

const AddMenu = ({ closeModal, refreshMenu }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isClicked, setIsClicked] = useState(false);
  const languages = getMenuLanguages(t);

  /** Main card: texts | display | manage */
  const [mainSectionTab, setMainSectionTab] = useState("texts");
  const [createdMenuId, setCreatedMenuId] = useState(null);

  const [menuDetails, setMenuDetails] = useState(
    languages.reduce((acc, lang) => {
      acc[lang.code] = {
        menuName: "",
        menuPriceCaption: "",
      };
      return acc;
    }, {})
  );

  const [file, setFile] = useState();
  const [imageUrl, setImageUrl] = useState(null);
  const [productImagesDisabled, setProductImagesDisabled] = useState(false);
  const [productPricesHidden, setProductPricesHidden] = useState(false);
  const [searchBarHidden, setSearchBarHidden] = useState(false);
  const [summaryButtonEnabled, setSummaryButtonEnabled] = useState(false);
  const [summaryTextAlign, setSummaryTextAlign] = useState("left");
  const [summaryShowPlusBetweenCategories, setSummaryShowPlusBetweenCategories] =
    useState(true);
  const [summaryBulletStyle, setSummaryBulletStyle] = useState("utensils");
  /** Prefix before product titles on customer app: none | dots | flower | food */
  const [productTitleIconStyle, setProductTitleIconStyle] = useState("none");
  /** Product card scale on customer app: small | normal | big */
  const [productCardSize, setProductCardSize] = useState("normal");
  /** Product/category/subcategory text fonts on customer app. */
  const [productTextFont, setProductTextFont] = useState("roboto");
  const [categoryTextFont, setCategoryTextFont] = useState("roboto");
  const [subCategoryTextFont, setSubCategoryTextFont] = useState("roboto");
  /** Center headings on customer app (category tabs default centered to match legacy). */
  const [categoryTitleAlign, setCategoryTitleAlign] = useState("center");
  const [subCategoryTitleAlign, setSubCategoryTitleAlign] = useState("left");
  const [productTitleAlign, setProductTitleAlign] = useState("left");
  /** Optional price for this menu (e.g. set menu); leave empty to omit. */
  const [menuPrice, setMenuPrice] = useState("");
  const [menuContentType, setMenuContentType] = useState(MENU_CONTENT_TYPES.INTERACTIVE);
  const [staticMenuMediaType, setStaticMenuMediaType] = useState(null);
  const [staticMenuFile, setStaticMenuFile] = useState(null);
  const [staticFilePreview, setStaticFilePreview] = useState(null);
  const [gateLang, setGateLang] = useState(languages[0]?.code || "en");

  const handleMenuLangInputChange = (lang, field, value) => {
    setMenuDetails({
      ...menuDetails,
      [lang]: {
        ...menuDetails[lang],
        [field]: value,
      },
    });
  };

  const createMenu = async () => {
    if (createdMenuId) return createdMenuId;

    setIsClicked(true);
    try {
      const token = localStorage.getItem("tokenLogin");
      if (!token) {
        swal("Error", "Vous devez être connecté pour créer un menu.", "error");
        return null;
      }

      const menuDetailsToSend = Object.entries(menuDetails)
        .map(([lang, details]) => ({
          language: lang,
          menuName: details.menuName?.trim(),
          menuPriceCaption: details.menuPriceCaption?.trim() || "",
        }))
        .filter((detail) => detail.menuName);

      if (!menuDetailsToSend.length) {
        swal("WARNING", t("menuFormTitleRequired"), "warning");
        return null;
      }

      if (menuContentType === MENU_CONTENT_TYPES.STATIC && !staticMenuFile) {
        swal(t("warning"), t("menuStaticFileRequired"), "warning");
        return null;
      }

      const rawPrice =
        menuPrice === "" || menuPrice === null || menuPrice === undefined
          ? null
          : Number(String(menuPrice).replace(",", "."));
      const safeMenuPrice =
        rawPrice !== null && Number.isFinite(rawPrice) && rawPrice >= 0
          ? rawPrice
          : null;

      const dataToSend = {
        menuDetails: menuDetailsToSend,
        productImagesDisabled,
        productPricesHidden,
        searchBarHidden,
        summaryButtonEnabled,
        summaryTextAlign,
        summaryShowPlusBetweenCategories,
        summaryBulletStyle,
        productTitleIconStyle,
        productCardSize,
        productTextFont,
        categoryTextFont,
        subCategoryTextFont,
        categoryTitleAlign,
        subCategoryTitleAlign,
        productTitleAlign,
        menuContentType,
        ...(menuContentType === MENU_CONTENT_TYPES.STATIC
          ? { staticMenuMediaType }
          : {}),
        ...(safeMenuPrice !== null ? { menuPrice: safeMenuPrice } : {}),
      };

      const formDataToSend = new FormData();
      formDataToSend.append("data", JSON.stringify(dataToSend));
      if (file) formDataToSend.append("file", file);
      if (menuContentType === MENU_CONTENT_TYPES.STATIC && staticMenuFile) {
        formDataToSend.append("staticFile", staticMenuFile);
      }

      const response = await axios.post(`${config.dev}/v2/menu/create`, formDataToSend, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });

      const newMenuId = extractCreatedMenuId(response.data);
      if (newMenuId) {
        setCreatedMenuId(newMenuId);
        refreshMenu?.();
      }
      return newMenuId;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        swal(
          "Unauthorized",
          "Your session has expired. Please log in again.",
          "error"
        );
      } else if (error.response?.status === 400) {
        swal(
          "Error",
          error.response.data?.message ||
            "There is already an active menu during this time period.",
          "error"
        );
      } else {
        swal(
          "Error",
          "Échec de la création du menu. Veuillez réessayer.",
          "error"
        );
      }
      return null;
    } finally {
      setIsClicked(false);
    }
  };

  const openManageSection = async () => {
    setMainSectionTab("manage");
    if (menuContentType === MENU_CONTENT_TYPES.STATIC) return;
    if (createdMenuId) return;
    const hasTitle = Object.values(menuDetails).some((d) => d?.menuName?.trim());
    if (!hasTitle) return;
    await createMenu();
  };

  const handleSelectTab = (key) => {
    if (!key) return;
    if (key === "manage") {
      openManageSection();
      return;
    }
    setMainSectionTab(key);
  };

  const handleSubmitMenu = async (e) => {
    e?.preventDefault?.();
    if (createdMenuId) {
      goBackToMenus();
      return;
    }

    const id = await createMenu();
    if (!id) return;

    if (menuContentType === MENU_CONTENT_TYPES.INTERACTIVE) {
      setMainSectionTab("manage");
      return;
    }

    swal(
      t("successTitle") || "Success",
      t("menuCreated") || "Menu created successfully!",
      "success"
    );
    goBackToMenus();
  };

  const goBackToMenus = () => {
    refreshMenu?.();
    closeModal?.();
    navigate("/menu");
  };

  const handleFinish = () => {
    goBackToMenus();
  };

  // Gérer les fichiers (image)
  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    const { isValid, message } = validateImageFile(selectedFile, 5);
    if (!isValid) {
      swal("Warning", message, "warning");
      event.target.value = "";
      return;
    }

    const updatedFiles = selectedFile;
    const updatedUrl = selectedFile ? URL.createObjectURL(selectedFile) : null;
    setFile(updatedFiles);
    setImageUrl(updatedUrl);
  };

  const handleRemoveImage = () => {
    setImageUrl(null);
    setFile(null);
  };

  const handleMenuContentTypeChange = (type) => {
    setMenuContentType(type);
    if (type === MENU_CONTENT_TYPES.INTERACTIVE) {
      if (staticFilePreview?.startsWith("blob:")) {
        URL.revokeObjectURL(staticFilePreview);
      }
      setStaticMenuFile(null);
      setStaticFilePreview(null);
      setStaticMenuMediaType(null);
    }
  };

  const handleStaticFileSelect = (event) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    const isPdf =
      selectedFile.type === "application/pdf" ||
      selectedFile.name.toLowerCase().endsWith(".pdf");
    const isImage = ["image/webp", "image/jpeg", "image/png"].includes(
      selectedFile.type
    );

    if (!isPdf && !isImage) {
      swal(t("warning"), t("menuStaticFileInvalid"), "warning");
      event.target.value = "";
      return;
    }

    if (selectedFile.size > 10 * 1024 * 1024) {
      swal(t("warning"), t("menuStaticFileTooLarge"), "warning");
      event.target.value = "";
      return;
    }

    if (staticFilePreview?.startsWith("blob:")) {
      URL.revokeObjectURL(staticFilePreview);
    }

    setStaticMenuFile(selectedFile);
    setStaticFilePreview(URL.createObjectURL(selectedFile));
    setMenuContentType(MENU_CONTENT_TYPES.STATIC);
    setStaticMenuMediaType(isPdf ? "pdf" : "image");
    event.target.value = "";
  };

  const handleRemoveStaticFile = () => {
    if (staticFilePreview?.startsWith("blob:")) {
      URL.revokeObjectURL(staticFilePreview);
    }
    setStaticMenuFile(null);
    setStaticFilePreview(null);
    setStaticMenuMediaType(null);
  };

  return (
    <>
      <PageTitle activeMenu={t("createMenu")} motherMenu={t("kitchen")} />
      <div className="container-fluid menu-form-page px-3 pb-5">
        <div className="row g-4">
          {mainSectionTab !== "manage" && (
          <div className="col-lg-4 col-xl-3">
            <Card className="menu-form-sidebar-card h-100">
              <Card.Header className="menu-form-sidebar-photo-header border-0 bg-white">
                {t("menuPhoto")}
              </Card.Header>
              <Card.Body className="menu-form-sidebar-photo pt-0">
                <div className="mt-1">
                  <label
                    className="btn btn-outline-secondary btn-sm"
                    style={{ cursor: "pointer" }}
                  >
                    <i className="fa fa-camera me-1" />
                    {t("uploadPhoto")}
                    <input
                      hidden
                      type="file"
                      accept={IMAGE_PICKER_ACCEPT}
                      onChange={(e) => handleFileChange(e)}
                    />
                  </label>
                </div>
                <div className="d-flex justify-content-center mt-3">
                  {imageUrl && (
                    <div
                      className="position-relative"
                      style={{ width: "120px", height: "120px" }}
                    >
                      <img
                        src={imageUrl}
                        alt=""
                        style={{
                          width: "120px",
                          height: "120px",
                          objectFit: "cover",
                          borderRadius: "12px",
                          border: "2px solid #FA8072",
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveImage()}
                        className="btn btn-danger position-absolute top-50 start-50 translate-middle rounded-circle p-0 d-flex align-items-center justify-content-center"
                        style={{
                          width: "36px",
                          height: "36px",
                          zIndex: 2,
                          boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                        }}
                        title={t("deleteImage")}
                      >
                        <i className="fa fa-trash" style={{ fontSize: "14px" }} />
                      </button>
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>
          </div>
          )}

          <div className={mainSectionTab === "manage" ? "col-12" : "col-lg-8 col-xl-9"}>
            <Card className="menu-form-shell">
              <Card.Body>
                {mainSectionTab === "texts" ? (
                  <>
                    <div className="menu-form-shell__title">{t("menuFormMainHeading")}</div>
                    <p className="menu-form-shell__subtitle">{t("menuFormMainSub")}</p>
                  </>
                ) : mainSectionTab === "manage" ? (
                  <>
                    <div className="menu-form-shell__title">{t("menuFormManageHeading")}</div>
                    <p className="menu-form-shell__subtitle">{t("menuFormManageSub")}</p>
                  </>
                ) : (
                  <>
                    <div className="menu-form-shell__title">{t("menuFormDisplayHeading")}</div>
                    <p className="menu-form-shell__subtitle">{t("menuFormDisplaySub")}</p>
                  </>
                )}

                <Tab.Container
                  activeKey={mainSectionTab}
                  onSelect={handleSelectTab}
                >
                  <Nav variant="tabs" className="menu-form-main-nav mb-3">
                    <Nav.Item>
                      <Nav.Link eventKey="texts">{t("menuFormTabTexts")}</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="display">{t("menuFormTabDisplay")}</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="manage">{t("menuFormTabManage")}</Nav.Link>
                    </Nav.Item>
                  </Nav>
                  <Tab.Content>
                    <Tab.Pane eventKey="texts">
                      <MenuContentTypeSection
                        menuContentType={menuContentType}
                        staticMenuMediaType={staticMenuMediaType}
                        staticMenuFile={null}
                        staticFilePreview={staticFilePreview}
                        onMenuContentTypeChange={handleMenuContentTypeChange}
                        onStaticFileSelect={handleStaticFileSelect}
                        onRemoveStaticFile={handleRemoveStaticFile}
                      />

                      <Tab.Container defaultActiveKey={languages[0].code}>
                        <Nav variant="pills" className="menu-form-lang-nav mb-3">
                          {languages.map((lang) => (
                            <Nav.Item key={lang.code}>
                              <Nav.Link eventKey={lang.code}>
                                <img width={22} height={16} src={lang.flag} alt="" className="me-1" />
                                {lang.label}
                              </Nav.Link>
                            </Nav.Item>
                          ))}
                        </Nav>
                        <Tab.Content>
                          {languages.map((lang) => (
                            <Tab.Pane eventKey={lang.code} key={lang.code}>
                              <div className="mb-3">
                                <div className="menu-form-field-label">{t("title")}</div>
                                <input
                                  type="text"
                                  className={`form-control form-control-lg ${
                                    lang.code === "ar" ? "text-end" : ""
                                  }`}
                                  placeholder={t("title")}
                                  value={menuDetails[lang.code].menuName}
                                  onChange={(e) =>
                                    handleMenuLangInputChange(
                                      lang.code,
                                      "menuName",
                                      e.target.value
                                    )
                                  }
                                />
                              </div>
                              <div className="mb-0">
                                <div className="menu-form-field-label">
                                  {t("menuPriceCaptionShort")}
                                </div>
                                <p className="menu-form-field-hint mb-2">
                                  {t("menuPriceCaptionSectionHint")}
                                </p>
                                <input
                                  type="text"
                                  className={`form-control ${
                                    lang.code === "ar" ? "text-end" : ""
                                  }`}
                                  placeholder={t("menuPriceCaptionPlaceholder")}
                                  value={menuDetails[lang.code].menuPriceCaption || ""}
                                  onChange={(e) =>
                                    handleMenuLangInputChange(
                                      lang.code,
                                      "menuPriceCaption",
                                      e.target.value
                                    )
                                  }
                                />
                              </div>
                            </Tab.Pane>
                          ))}
                        </Tab.Content>
                      </Tab.Container>

                      <div className="menu-form-optional-price">
                        <div className="menu-form-field-label">
                          {t("menuPriceLabel")}{" "}
                          <span className="text-muted fw-normal small">
                            ({t("menuPriceOptionalHint")})
                          </span>
                        </div>
                        <input
                          type="number"
                          className="form-control"
                          min={0}
                          step="0.01"
                          placeholder={t("menuPricePlaceholder")}
                          value={menuPrice}
                          onChange={(e) => {
                            const v = e.target.value.replace(",", ".");
                            if (
                              v === "" ||
                              (!Number.isNaN(Number(v)) && Number(v) >= 0)
                            ) {
                              setMenuPrice(v);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "-" || e.key === "e") e.preventDefault();
                          }}
                        />
                      </div>
                    </Tab.Pane>
                    <Tab.Pane eventKey="display">
                      <div className="menu-form-display-tab">
                        <MenuFormDisplaySection
                          step={1}
                          title={t("menuFormDisplaySectionVisibility")}
                          hint={t("menuFormDisplaySectionVisibilityHint")}
                        >
                          <MenuCustomerDisplaySwitches
                            imagesShown={!productImagesDisabled}
                            pricesShown={!productPricesHidden}
                            searchShown={!searchBarHidden}
                            summaryEnabled={summaryButtonEnabled}
                            onImagesShownChange={(shown) =>
                              setProductImagesDisabled(!shown)
                            }
                            onPricesShownChange={(shown) =>
                              setProductPricesHidden(!shown)
                            }
                            onSearchShownChange={(shown) =>
                              setSearchBarHidden(!shown)
                            }
                            onSummaryEnabledChange={setSummaryButtonEnabled}
                            idPrefix="add-menu"
                          />
                        </MenuFormDisplaySection>

                        <MenuFormDisplaySection
                          step={2}
                          title={t("menuFormDisplaySectionSummary")}
                          hint={t("menuFormDisplaySectionSummaryHint")}
                        >
                          <MenuSummaryAlignPicker
                            value={summaryTextAlign}
                            onChange={setSummaryTextAlign}
                            idPrefix="add-menu-summary-align"
                          />
                          <MenuSummaryBulletPicker
                            value={summaryBulletStyle}
                            onChange={setSummaryBulletStyle}
                            idPrefix="add-menu-summary-bullet"
                          />
                          <MenuSummaryPlusPicker
                            value={summaryShowPlusBetweenCategories}
                            onChange={setSummaryShowPlusBetweenCategories}
                            idPrefix="add-menu-summary-plus"
                          />
                        </MenuFormDisplaySection>

                        <MenuFormDisplaySection
                          step={3}
                          title={t("menuFormDisplaySectionTitles")}
                          hint={t("menuFormDisplaySectionTitlesHint")}
                        >
                          <MenuTitlesAlignPickers
                            categoryTitleAlign={categoryTitleAlign}
                            subCategoryTitleAlign={subCategoryTitleAlign}
                            productTitleAlign={productTitleAlign}
                            onCategoryTitleAlignChange={setCategoryTitleAlign}
                            onSubCategoryTitleAlignChange={setSubCategoryTitleAlign}
                            onProductTitleAlignChange={setProductTitleAlign}
                            idPrefix="add-menu-titles-align"
                          />
                        </MenuFormDisplaySection>

                        <MenuFormDisplaySection
                          step={4}
                          title={t("menuFormDisplaySectionStyle")}
                          hint={t("menuFormDisplaySectionStyleHint")}
                        >
                          <div className="menu-form-display-style-grid">
                            <ProductTitleIconStylePicker
                              value={productTitleIconStyle}
                              onChange={setProductTitleIconStyle}
                              idPrefix="add-menu"
                            />
                            <ProductCardSizePicker
                              value={productCardSize}
                              onChange={setProductCardSize}
                              idPrefix="add-menu"
                            />
                          </div>
                          <MenuTextFontPicker
                            productTextFont={productTextFont}
                            categoryTextFont={categoryTextFont}
                            subCategoryTextFont={subCategoryTextFont}
                            onProductTextFontChange={setProductTextFont}
                            onCategoryTextFontChange={setCategoryTextFont}
                            onSubCategoryTextFontChange={setSubCategoryTextFont}
                            idPrefix="add-menu"
                            compact
                          />
                        </MenuFormDisplaySection>
                      </div>
                    </Tab.Pane>
                    <Tab.Pane eventKey="manage">
                      {menuContentType !== MENU_CONTENT_TYPES.INTERACTIVE ? (
                        <p className="menu-form-shell__subtitle mb-0">{t("menuFormManageStatic")}</p>
                      ) : createdMenuId ? (
                        <div className="menu-form-manage">
                          <MenuTree
                            menuId={createdMenuId}
                            onEditMenu={() => setMainSectionTab("texts")}
                            onBackToMenus={handleFinish}
                          />
                        </div>
                      ) : isClicked ? (
                        <div className="menu-form-manage-loading">
                          <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">{t("loading")}</span>
                          </div>
                          <span>{t("menuFormManageCreating")}</span>
                        </div>
                      ) : (
                        <div className="menu-form-manage-gate">
                          <p className="menu-form-shell__subtitle mb-0">
                            {t("menuFormManageCreateHint")}
                          </p>
                          <Tab.Container
                            activeKey={gateLang}
                            onSelect={(key) => key && setGateLang(key)}
                          >
                            <Nav variant="pills" className="menu-form-lang-nav mb-0">
                              {languages.map((lang) => (
                                <Nav.Item key={lang.code}>
                                  <Nav.Link
                                    eventKey={lang.code}
                                    className={
                                      menuDetails[lang.code]?.menuName?.trim()
                                        ? "has-value"
                                        : ""
                                    }
                                  >
                                    <img
                                      width={22}
                                      height={16}
                                      src={lang.flag}
                                      alt=""
                                      className="me-1"
                                    />
                                    {lang.label}
                                  </Nav.Link>
                                </Nav.Item>
                              ))}
                            </Nav>
                            <Tab.Content>
                              {languages.map((lang) => (
                                <Tab.Pane eventKey={lang.code} key={lang.code}>
                                  <div className="menu-form-field-label">{t("title")}</div>
                                  <input
                                    type="text"
                                    className={`form-control form-control-lg ${
                                      lang.code === "ar" ? "text-end" : ""
                                    }`}
                                    placeholder={t("title")}
                                    value={menuDetails[lang.code]?.menuName || ""}
                                    onChange={(e) =>
                                      handleMenuLangInputChange(
                                        lang.code,
                                        "menuName",
                                        e.target.value
                                      )
                                    }
                                  />
                                </Tab.Pane>
                              ))}
                            </Tab.Content>
                          </Tab.Container>
                          <button
                            type="button"
                            className="btn btn-primary px-4"
                            onClick={async () => {
                              const id = await createMenu();
                              if (id) setMainSectionTab("manage");
                            }}
                            disabled={isClicked}
                          >
                            {t("createMenuAndManage")}
                          </button>
                        </div>
                      )}
                    </Tab.Pane>
                  </Tab.Content>
                </Tab.Container>

                {!(mainSectionTab === "manage" && !createdMenuId) && (
                <div className="d-flex justify-content-end gap-2 pt-4 mt-2 border-top border-light">
                  {createdMenuId ? (
                    <button
                      type="button"
                      className="btn btn-primary px-4"
                      onClick={handleFinish}
                    >
                      {t("prodFormDone")}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-primary px-4"
                      onClick={handleSubmitMenu}
                      disabled={isClicked}
                    >
                      {menuContentType === MENU_CONTENT_TYPES.INTERACTIVE
                        ? t("createMenuAndManage")
                        : t("createMenu")}
                    </button>
                  )}
                </div>
                )}
              </Card.Body>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddMenu;
